/**
 * PastTimeOverlay — non-interactive shading for time slots that are already
 * in the past inside week / day grids.
 *
 * Rules:
 *   - Past day (column entirely before today): height covers the whole 24 h
 *     column, no "now line".
 *   - Today's column: height covers only the portion before "now"; the bottom
 *     edge gets a 1 px coloured line — the calendar's "now" indicator.
 *   - Future day: caller should not render this component at all.
 *
 * The overlay sits absolutely-positioned inside the day column above the hour
 * grid lines but below the event blocks and time-slot drop targets, with
 * `pointer-events: none` so drags through past slots still work (placing a
 * task in the past is intentional behaviour for backdating completions).
 */

import React from 'react'

interface PastTimeOverlayProps {
  /** Pixel height to shade. */
  heightPx: number
  /** When true, draws a 1 px "now line" at the overlay's bottom edge. */
  showNowLine?: boolean
}

export const PastTimeOverlay: React.FC<PastTimeOverlayProps> = ({
  heightPx,
  showNowLine = false,
}) => {
  if (heightPx <= 0) return null

  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 pointer-events-none"
      style={{
        height: `${heightPx}px`,
        background:
          'repeating-linear-gradient(135deg, rgba(127,127,127,0.06) 0 6px, rgba(127,127,127,0.10) 6px 12px)',
      }}
    >
      {showNowLine && (
        <div
          className="absolute left-0 right-0"
          style={{
            top: `${Math.max(heightPx - 1, 0)}px`,
            height: '2px',
            background: 'var(--semantic-error)',
            boxShadow:
              '0 0 0 1px color-mix(in srgb, var(--semantic-error) 25%, transparent)',
          }}
        >
          <div
            className="absolute -left-1 -top-1 w-2.5 h-2.5 rounded-full"
            style={{ background: 'var(--semantic-error)' }}
          />
        </div>
      )}
    </div>
  )
}

export default PastTimeOverlay
